/** biome-ignore-all lint/correctness/useUniqueElementIds: this should be ok */
import {FC, useCallback, useState} from 'react';
import {styled} from 'styled-components';

import {Button} from '@shared_web/components/core/button';
import {showAlert, showSuccess} from '@shared_web/components/core/notifications';

import {TableWrapper} from '@mavismart_frontend/components/common_styles';
import {ToggleSwitch} from '@mavismart_frontend/components/toggle_switch';
import {translate, Translate} from '@mavismart_frontend/components/translate';
import {apiCall} from '@mavismart_frontend/lib/api';
import {BUTTON_THEME, SELECT_THEME, SELECT_WRAPPER_THEME} from '@mavismart_frontend/lib/constants';
import {GenericCell} from '@mavismart_frontend/pages/cells/generic_cell';
import {getUserLanguage, setAccount, useAccount} from '@mavismart_frontend/stores/account_store';

type NotificationEmailFrequency = 'never' | 'monthly' | 'weekly' | 'daily' | 'hourly';
const NotificationEmailFrequencies: NotificationEmailFrequency[] = [
  'never',
  'monthly',
  'weekly',
  'daily',
  'hourly',
];

export const NotificationsParams: FC = () => {
  const account = useAccount();
  const [notifState, setNotifState] = useState<{
    alertNotificationsEnabled: boolean;
    anomalyNotificationsEnabled: boolean;
    notificationEmailFrequency: NotificationEmailFrequency | undefined;
  }>({
    alertNotificationsEnabled: account?.alertNotificationsEnabled ?? false,
    anomalyNotificationsEnabled: account?.anomalyNotificationsEnabled ?? false,
    notificationEmailFrequency: account?.notificationEmailFrequency ?? 'never',
  });

  const [loading, setLoading] = useState(false);

  const updateNotif = useCallback(
    (key: keyof typeof notifState, value: boolean | NotificationEmailFrequency) => {
      const newState = {...notifState, [key]: value};
      setNotifState(newState);
    },
    [notifState]
  );

  const handleAlertToggleChange = useCallback(
    (checked: boolean) => {
      updateNotif('alertNotificationsEnabled', checked);
    },
    [updateNotif]
  );

  const handleAnomalyToggleChange = useCallback(
    (checked: boolean) => {
      updateNotif('anomalyNotificationsEnabled', checked);
    },
    [updateNotif]
  );

  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value as NotificationEmailFrequency;
      updateNotif('notificationEmailFrequency', value);
    },
    [updateNotif]
  );

  const handleSave = useCallback(async () => {
    // Save the notification settings
    try {
      setLoading(true);
      const res = await apiCall('POST /update-account', notifState);
      setAccount({
        ...res,
        language: res.language ?? getUserLanguage(undefined),
      });
      showSuccess(translate('notification_settings_updated', getUserLanguage(account)));
    } catch {
      showAlert(translate('error_saving_notification_settings', getUserLanguage(account)));
    } finally {
      setLoading(false);
    }
  }, [notifState, account]);

  return (
    <NotificationsPageWrapper>
      <TableWrapper>
        <GenericCell
          id="alertNotificationsEnabled"
          title={<Translate value="activate_notifications_alerts" />}
          subtitle={<Translate value="receive_red_notifications" />}
          rightIcon={
            <ToggleSwitch
              checked={notifState.alertNotificationsEnabled}
              onChange={handleAlertToggleChange}
            />
          }
        />
        <GenericCell
          id="anomalyNotificationsEnabled"
          title={<Translate value="activate_notifications_anomalies" />}
          subtitle={<Translate value="receive_yellow_notifications" />}
          rightIcon={
            <ToggleSwitch
              checked={notifState.anomalyNotificationsEnabled}
              onChange={handleAnomalyToggleChange}
            />
          }
        />
        {account?.hasNotificationEmail === true ? (
          <GenericCell
            id="notificationEmailFrequency"
            title={<Translate value="activate_notifications_by_email" />}
            subtitle={<Translate value="receive_notifications_by_email" />}
            rightIcon={
              <SelectWrapper>
                <StyledSelect
                  value={notifState.notificationEmailFrequency}
                  onChange={handleSelectChange}
                >
                  {NotificationEmailFrequencies.map(freq => (
                    <option key={freq} value={freq}>
                      <Translate value={freq} />
                    </option>
                  ))}
                </StyledSelect>
              </SelectWrapper>
            }
          />
        ) : (
          <></>
        )}
      </TableWrapper>
      <WrapperButtons>
        <Button themeOverrides={BUTTON_THEME} onClickAsync={handleSave} loading={loading}>
          <Translate value="save" />
        </Button>
      </WrapperButtons>
    </NotificationsPageWrapper>
  );
};

NotificationsParams.displayName = 'NotificationsParams';

const WrapperButtons = styled.div`
  display: flex;
  justify-content: center;
`;

const NotificationsPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  height: 100%;
`;

const StyledSelect = styled.select`
  width: 114px;
  padding: 10px 40px 10px 12px;
  font-size: 14px;
  ${SELECT_THEME}
  `;

const SelectWrapper = styled.div`
     ${SELECT_WRAPPER_THEME}
`;
